
import { useState } from 'react';
import { PaymentService, UserPackageService } from '@/integrations/supabase/services';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export const usePayment = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const createPayment = async (packageType: string) => {
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "Please log in to continue.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Calculate amount based on package type
      const packageAmounts = {
        free: 0,
        basic: 1299,
        standard: 2199,
        premium: 3499
      };

      const amount = packageAmounts[packageType as keyof typeof packageAmounts] || 0;

      // Create payment record
      await PaymentService.add({
        user_id: user.uid,
        package_type: packageType,
        amount: amount,
        status: packageType === 'free' ? 'completed' : 'pending'
      });

      // Update user package
      await UserPackageService.upsert(user.uid, packageType, 'active');

      if (packageType === 'free') {
        toast({
          title: "Package Selected",
          description: "Free package selected successfully!",
        });
        return { success: true, packageType };
      } else {
        // Redirect to Stripe payment for paid packages
        const stripeUrls = {
          basic: 'https://buy.stripe.com/9B6aEP9fb52gbHS6Cb8so02',
          standard: 'https://buy.stripe.com/28E4gr62Z0M0bHS6Cb8so03',
          premium: 'https://buy.stripe.com/cNi3cn773fGU13egcL8so04'
        };

        const stripeUrl = stripeUrls[packageType as keyof typeof stripeUrls];
        if (stripeUrl) {
          window.open(stripeUrl, '_blank');
        }

        toast({
          title: "Payment Initiated",
          description: "Redirecting to payment page...",
        });
        return { success: true, packageType };
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Error",
        description: "Failed to process package selection. Please try again.",
        variant: "destructive",
      });
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const selectFreePackage = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Save free package selection
      await UserPackageService.upsert(user.uid, 'free', 'active');
      
      toast({
        title: "Free Package Selected",
        description: "You have selected the free consultation package.",
      });

      return { success: true };
    } catch (error) {
      console.error('Package selection error:', error);
      toast({
        title: "Error",
        description: "Failed to select package. Please try again.",
        variant: "destructive",
      });
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (sessionId: string) => {
    try {
      // For now, return success
      // You can integrate with Stripe webhook verification later
      return { success: true, status: 'paid' };
    } catch (error) {
      console.error('Payment verification error:', error);
      return { success: false };
    }
  };

  return {
    createPayment,
    selectFreePackage,
    verifyPayment,
    loading
  };
};
